import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { DateBlock } from '@/components/ui/DateBlock';
import { Modal } from '@/components/ui/Modal';
import { Checkbox } from '@/components/ui/Checkbox';
import { Radio } from '@/components/ui/Radio';
import { Textarea } from '@/components/ui/Textarea';
import { Input } from '@/components/ui/Input';
import { uiLocale } from '@/lib/i18n';
import { TYPE } from '@/lib/typography';
import { appointmentDateLocale, formatAppointmentTimeRange } from '@/utils/appointmentTimeFormat';
import { cn } from '@/utils/cn';
import { possessiveName } from '@/utils/possessiveName';
import type { CancellationOverrideChoice } from '@/lib/cancellationOverride';

export type CancelScope = 'single' | 'all' | 'future';

export function CancelAppointmentModal({
  open,
  onClose,
  onConfirm,
  pending = false,
  patientName,
  serviceName,
  startsAt,
  endsAt,
  clinicianName,
  isRecurringSeries = false,
  cancelScope = 'single',
  onCancelScopeChange,
  reason,
  onReasonChange,
  sendEmail,
  onSendEmailChange,
  sendEmailDisabledReason,
  seriesKind = 'appointment',
  subject = 'appointment',
  allowOverride = false,
  overrideChoice = 'keep_policy',
  onOverrideChoiceChange,
  overrideReason = '',
  onOverrideReasonChange,
  overrideAmount = '',
  onOverrideAmountChange,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  pending?: boolean;
  patientName?: string | null;
  serviceName?: string | null;
  startsAt?: string | Date | null;
  endsAt?: string | Date | null;
  clinicianName?: string | null;
  isRecurringSeries?: boolean;
  cancelScope?: CancelScope;
  onCancelScopeChange?: (scope: CancelScope) => void;
  reason?: string;
  onReasonChange?: (reason: string) => void;
  sendEmail?: boolean;
  onSendEmailChange?: (value: boolean) => void;
  sendEmailDisabledReason?: string | null;
  seriesKind?: 'appointment' | 'block';
  /** A block is not an appointment cancellation. */
  subject?: 'appointment' | 'block';
  allowOverride?: boolean;
  overrideChoice?: CancellationOverrideChoice;
  onOverrideChoiceChange?: (choice: CancellationOverrideChoice) => void;
  overrideReason?: string;
  onOverrideReasonChange?: (reason: string) => void;
  overrideAmount?: string;
  onOverrideAmountChange?: (amount: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [more, setMore] = useState(false);
  const start = startsAt ? new Date(startsAt) : null;
  const end = endsAt ? new Date(endsAt) : start;
  const locale = appointmentDateLocale(i18n.language);
  const weekday = start ? new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(start) : '';
  const month = start ? new Intl.DateTimeFormat(locale, { month: 'short' }).format(start) : '';
  const dateLine = start
    ? new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(
        start,
      )
    : '';
  const timeLine = start && end ? formatAppointmentTimeRange(start, end, i18n.language) : '';
  const meta = [dateLine, timeLine, clinicianName].filter(Boolean).join(' · ');

  if (subject === 'block') {
    return (
      <Modal
        open={open}
        onClose={onClose}
        tone="danger"
        showIcon
        role="alertdialog"
        title={t('appointments.modal.block_remove_title')}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={onConfirm}
              loading={pending}
              loadingText={t('appointments.modal.block_remove_confirming')}
            >
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        {isRecurringSeries && onCancelScopeChange ? (
          <fieldset className="space-y-s2">
            <legend className={cn(TYPE.label, 'text-ds-text')}>
              {t('appointments.modal.block_scope')}
            </legend>
            <Radio
              id="block-remove-scope-single"
              name="blockRemoveScope"
              checked={cancelScope === 'single'}
              onChange={() => onCancelScopeChange('single')}
              label={t('appointments.modal.block_scope_single')}
            />
            <Radio
              id="block-remove-scope-all"
              name="blockRemoveScope"
              checked={cancelScope === 'all'}
              onChange={() => onCancelScopeChange('all')}
              label={t('appointments.modal.block_scope_all')}
            />
            <Radio
              id="block-remove-scope-future"
              name="blockRemoveScope"
              checked={cancelScope === 'future'}
              onChange={() => onCancelScopeChange('future')}
              label={t('appointments.modal.block_scope_future')}
            />
          </fieldset>
        ) : null}
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      tone="danger"
      showIcon
      role="alertdialog"
      title={t('appointments.cancel_modal.title')}
      description={t('appointments.cancel_modal.body', {
        name: possessiveName(patientName, uiLocale()),
      })}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            {t('appointments.cancel_modal.keep')}
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={onConfirm}
            loading={pending}
            loadingText={t('appointments.cancel_modal.confirming')}
          >
            {t('appointments.cancel_modal.confirm')}
          </Button>
        </>
      }
    >
      {start ? (
        <div className="flex items-center gap-s3 rounded-pop bg-ds-bg p-s3">
          <DateBlock weekday={weekday} day={start.getDate()} month={month} />
          <div className="min-w-0">
            <p className={cn(TYPE.section, 'text-ds-text')}>
              {serviceName || t('appointments.no_service')}
            </p>
            <p className={cn(TYPE.small, 'mt-0.5 tabular-nums text-ds-muted')}>{meta}</p>
          </div>
        </div>
      ) : null}

      {isRecurringSeries && onCancelScopeChange ? (
        <fieldset className="mt-s4 space-y-s2">
          <legend className={cn(TYPE.label, 'text-ds-text')}>
            {t(
              seriesKind === 'block'
                ? 'appointments.modal.block_scope'
                : 'appointments.recurrence.confirm_cancel_series',
            )}
          </legend>
          <Radio
            id="cancel-scope-single"
            name="cancelScope"
            checked={cancelScope === 'single'}
            onChange={() => onCancelScopeChange('single')}
            label={t(
              seriesKind === 'block'
                ? 'appointments.modal.block_scope_single'
                : 'appointments.recurrence.cancel_single',
            )}
          />
          <Radio
            id="cancel-scope-all"
            name="cancelScope"
            checked={cancelScope === 'all'}
            onChange={() => onCancelScopeChange('all')}
            label={t(
              seriesKind === 'block'
                ? 'appointments.modal.block_scope_all'
                : 'appointments.recurrence.cancel_all',
            )}
          />
          <Radio
            id="cancel-scope-future"
            name="cancelScope"
            checked={cancelScope === 'future'}
            onChange={() => onCancelScopeChange('future')}
            label={t(
              seriesKind === 'block'
                ? 'appointments.modal.block_scope_future'
                : 'appointments.recurrence.cancel_future',
            )}
          />
        </fieldset>
      ) : null}

      {onReasonChange ? (
        <div className="mt-s4">
          <Textarea
            id="cancel-reason"
            label={t('appointments.modal.cancel_reason')}
            value={reason ?? ''}
            onChange={(event) => onReasonChange(event.target.value)}
            rows={2}
          />
        </div>
      ) : null}

      {onSendEmailChange ? (
        <div className="mt-s4">
          <Checkbox
            id="cancel-send-email"
            checked={Boolean(sendEmail) && !sendEmailDisabledReason}
            disabled={Boolean(sendEmailDisabledReason)}
            onChange={(event) => onSendEmailChange(event.target.checked)}
            label={t('appointments.cancel_modal.send_email')}
            hint={sendEmailDisabledReason ?? t('appointments.cancel_modal.send_email_hint')}
          />
        </div>
      ) : null}

      <p className={cn(TYPE.small, 'mt-s4 text-ds-muted')}>
        {t('appointments.cancel_modal.payment')}
      </p>

      {allowOverride && subject === 'appointment' ? (
        <div className="mt-s4">
          <Button type="button" variant="tertiary" onClick={() => setMore((open) => !open)}>
            {t('appointments.override.more')}
          </Button>
          {more ? (
            <fieldset className="mt-s3 space-y-s2">
              <legend className={cn(TYPE.label, 'text-ds-text')}>
                {t('appointments.override.legend')}
              </legend>
              {(
                [
                  'keep_policy',
                  'full_refund',
                  'partial_refund',
                  'restore_clip',
                  'waive_fee',
                ] as const
              ).map((choice) => (
                <Radio
                  key={choice}
                  id={`cancel-override-${choice}`}
                  name="cancelOverride"
                  checked={overrideChoice === choice}
                  onChange={() => onOverrideChoiceChange?.(choice)}
                  label={t(`appointments.override.${choice}`)}
                />
              ))}
              {overrideChoice !== 'keep_policy' ? (
                <Textarea
                  id="cancel-override-reason"
                  label={t('appointments.override.reason')}
                  value={overrideReason}
                  onChange={(event) => onOverrideReasonChange?.(event.target.value)}
                  rows={2}
                />
              ) : null}
              {overrideChoice === 'partial_refund' ? (
                <Input
                  id="cancel-override-amount"
                  label={t('appointments.override.amount')}
                  inputMode="decimal"
                  value={overrideAmount}
                  onChange={(event) => onOverrideAmountChange?.(event.target.value)}
                />
              ) : null}
            </fieldset>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
